<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {FormBeneficialOwnerPerson} from "@/domains/home/services/nir/forms/components/widgets/types";
import BeneficialOwner from "@/domains/home/services/nir/forms/components/widgets/BeneficialOwner.vue";

const props = defineProps({
  cardinality: {
    type: String,
    required: true,
    default: 'SINGLE',
  },
  active: Boolean,
  modelValue: {
    type: Array as () => FormBeneficialOwnerPerson[],
    default: () => [],
  },
  data: {
    type: Array as () => FormBeneficialOwnerPerson[],
    default: () => [],
  },
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: FormBeneficialOwnerPerson[]): void
}>()

const beneficialOwners = ref<FormBeneficialOwnerPerson[]>([
  {
    givenName: '',
    surname: '',
    middleNames: '',
    gender: '',
    dateOfBirth: '',
    nationalityId: '',
    idType: '',
    idNumber: '',
    email: '',
    phone: '',
    countryId: '',
    votingRightsPercentage: 0,
    ownershipPercentage: 0,
    address: {
      districtId: '',
      countyId: '',
      subCountyId: '',
      parishId: '',
      villageId: '',
      description: '',
    },
  },
])

watch(
  beneficialOwners,
  (newValue) => {
    emit('update:modelValue', newValue)
  },
  { deep: true },
)

watch(
  () => props.data,
  () => {
    beneficialOwners.value = props.data
  },
)

function addBeneficialOwner() {
  beneficialOwners.value.push({
    givenName: '',
    surname: '',
    middleNames: '',
    nationalityId: '',
    gender: '',
    dateOfBirth: '',
    idType: '',
    idNumber: '',
    email: '',
    phone: '',
    countryId: '',
    votingRightsPercentage: 0,
    ownershipPercentage: 0,
    address: {
      districtId: '',
      countyId: '',
      subCountyId: '',
      parishId: '',
      villageId: '',
      description: '',
    },
  })
}

function removeBeneficialOwner(index: number) {
  if (index > 0) {
    beneficialOwners.value.splice(index, 1)
  }
}

function updateOwner(index: number, value: FormBeneficialOwnerPerson) {
  beneficialOwners.value[index] = value
}

const totalVotingRights = computed(() =>
  beneficialOwners.value.reduce((sum, o) => sum + (Number(o.votingRightsPercentage) || 0), 0),
)

const totalOwnership = computed(() =>
  beneficialOwners.value.reduce((sum, o) => sum + (Number(o.ownershipPercentage) || 0), 0),
)

const votingRightsError = computed(() =>
  totalVotingRights.value !== 100
    ? `Total voting rights must equal 100% (currently ${totalVotingRights.value}%).`
    : '',
)

const ownershipError = computed(() =>
  totalOwnership.value > 100
    ? `Total ownership must not exceed 100% (currently ${totalOwnership.value}%).`
    : '',
)
</script>

<template>
  <div class="flex flex-col space-y-3 border border-gray-200 rounded-lg p-2">
    <div v-if="votingRightsError || ownershipError" class="flex flex-col gap-1">
      <div
        v-if="votingRightsError"
        class="flex items-start gap-2 bg-danger-50 border border-danger-200 text-danger-700 text-xs rounded px-3 py-2"
      >
        <i class="fa-solid fa-circle-exclamation mt-0.5 shrink-0"></i>
        <span>{{ votingRightsError }}</span>
      </div>
      <div
        v-if="ownershipError"
        class="flex items-start gap-2 bg-danger-50 border border-danger-200 text-danger-700 text-xs rounded px-3 py-2"
      >
        <i class="fa-solid fa-circle-exclamation mt-0.5 shrink-0"></i>
        <span>{{ ownershipError }}</span>
      </div>
    </div>

    <div v-for="(owner, index) in beneficialOwners" :key="index">
      <div class="flex items-center justify-between mb-3">
        <span class="text-sm font-semibold text-gray-700">
          <i class="fa-solid fa-user-tie mr-2 text-gray-400"></i>
          Beneficial Owner {{ cardinality === 'SINGLE' ? '' : index + 1 }}
        </span>
        <button
          v-if="index > 0"
          type="button"
          @click="removeBeneficialOwner(index)"
          class="btn-sm-primary"
          title="Remove beneficial owner"
        >
          <i class="fa-solid fa-trash"></i>
        </button>
      </div>

      <BeneficialOwner :modelValue="owner" @update:modelValue="updateOwner(index, $event)" />
    </div>

    <!-- Add Beneficial Owner Button -->
    <button
      v-if="cardinality === 'MULTIPLE'"
      type="button"
      @click="addBeneficialOwner"
      class="btn-dashed"
    >
      <i class="fa-solid fa-plus"></i>
      <span>Add Another Beneficial Owner</span>
    </button>
  </div>
</template>

<style scoped></style>
